import { useLayoutEffect, useRef } from 'react';

/**
 * THE WEEK SLIDE — a continuous strip of weeks (owner 2026-10-30, in three messages):
 *  - *"when changing week, clicking left and right on the date picker on desktop can we have the
 *    time sheet below slide/animate to show the next week sliding into view"*;
 *  - *"slower slide. ITs too fast"* → 450 ms;
 *  - *"theres a flicker as if it reloads"* and *"can the old week fully scroll off as the next one
 *    slides in so its like a contiouse strip of dates?"*.
 *
 * `useWeekSlide(shownWeek, requestedWeek)` → a ref for the element holding the week's content.
 *  - `shownWeek` — the week the CONTENT shows (the loaded payload's `week_start`);
 *  - `requestedWeek` — the picker's week.
 * How: the moment the picker moves, the current week is SNAPSHOTTED (a static `cloneNode`, nothing
 * is mounted). When the new week's rows arrive, the snapshot is laid over the old spot and BOTH
 * move together — the old week fully off one side, the new week fully in from the other — then the
 * snapshot is removed. Nothing fades, nothing is ever blank, and the real content never remounts,
 * so an open row, a fold or a draft is untouched.
 * 🪤 The snapshot must be taken at the CLICK: by the time the new rows are here React has already
 * replaced the old ones in place.
 * The parent is clipped sideways (`overflow-x: clip`) only while the strip moves, then restored.
 * 📱 PHONES TOO since 2026-10-31 (owner: the end-panel step "should scroll the same direction as dragged";
 * the snapshot keeps each inner scroller's scrollLeft, so the day strip slides off as it was seen).
 * ♿ Never under `prefers-reduced-motion`; the first render and an unchanged
 * week never animate. jsdom has no `Element.animate`, so tests stub it.
 */
export const WEEK_SLIDE_MS = 450;
const EASE = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const GAP_PX = 24; // a sliver of space between the two weeks on the strip

const mq = (q) => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
const motionOk = () => !mq('(prefers-reduced-motion: reduce)');
const off = (dir) => `translateX(calc(${dir * 100}% + ${dir * GAP_PX}px))`;

export default function useWeekSlide(shownWeek, requestedWeek) {
  const ref = useRef(null);
  const prevShown = useRef(shownWeek);
  const snapshot = useRef(null); // { node, rect } of the week on screen when the picker moved
  const running = useRef(null); // cleanup of the strip in flight

  // ORDER MATTERS: the strip runs FIRST so it consumes the snapshot before the photo effect below
  // (which clears it once picker and content agree) sees the arrival render.
  // The new week's rows are here: run the strip.
  useLayoutEffect(() => {
    const before = prevShown.current;
    prevShown.current = shownWeek;
    const shot = snapshot.current;
    snapshot.current = null;
    if (running.current) running.current();
    const el = ref.current;
    if (!el || !shownWeek || !before || shownWeek === before || typeof el.animate !== 'function' || !motionOk()) return;
    // A hidden panel (the manager's other tab) has nothing to show: no strip, no snapshot left behind.
    if (el.closest('[hidden]')) return;
    const dir = shownWeek > before ? 1 : -1; // ISO dates compare as strings
    const parent = el.parentElement;
    const timing = { duration: WEEK_SLIDE_MS, easing: EASE };
    let ghost = null;
    const saved = parent ? { position: parent.style.position, overflowX: parent.style.overflowX } : null;
    if (parent && shot) {
      const pos = typeof window !== 'undefined' ? window.getComputedStyle(parent).position : 'static';
      if (!pos || pos === 'static') parent.style.position = 'relative'; // '' = jsdom's "static"
      parent.style.overflowX = 'clip';
      ghost = shot.node;
      Object.assign(ghost.style, {
        // Measured NOW, after the parent became the positioning box (the old week sat exactly where
        // the new one sits: same element, same place).
        position: 'absolute', top: `${el.offsetTop}px`, left: `${el.offsetLeft}px`, width: `${el.offsetWidth}px`,
        // No inline pointer-events (dirtyRowsStayClickable ratchet §4): `inert` already takes the
        // snapshot out of hit-testing and focus.
        margin: '0',
      });
      parent.appendChild(ghost);
      // A clone starts every scroller at 0: put each one back where it was seen (the phone day strip).
      if (shot.scrolled && shot.scrolled.length) {
        const all = ghost.querySelectorAll('*');
        shot.scrolled.forEach(([i, x]) => { if (all[i]) all[i].scrollLeft = x; });
      }
    }
    const ghostAnim = ghost ? ghost.animate([{ transform: 'translateX(0)' }, { transform: off(-dir) }], { ...timing, fill: 'forwards' }) : null;
    const anim = el.animate([{ transform: off(dir) }, { transform: 'translateX(0)' }], timing);
    // 🪤 QUICK CLICKS (review 2026-10-30, measured in a browser): strip 1's `finished` resolves in the
    // MIDDLE of strip 2, and an unguarded cleanup then reset the parent's styles and stranded ghost 2
    // against the wrong ancestor. So a cleanup acts only while its own strip is the running one, and a
    // new strip ends the old one first (cancelling both of its animations).
    const cleanup = () => {
      if (running.current !== cleanup) return;
      running.current = null;
      if (ghostAnim && typeof ghostAnim.cancel === 'function') ghostAnim.cancel();
      if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
      if (parent && saved) { parent.style.position = saved.position; parent.style.overflowX = saved.overflowX; }
    };
    running.current = cleanup;
    if (anim && anim.finished && typeof anim.finished.then === 'function') anim.finished.then(cleanup, cleanup);
    else if (typeof window !== 'undefined') window.setTimeout(cleanup, WEEK_SLIDE_MS + 50);
  }, [shownWeek]);

  // The picker moved away from the week on screen: photograph it now.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !requestedWeek || !shownWeek) return;
    if (requestedWeek === shownWeek) { snapshot.current = null; return; }
    if (snapshot.current || typeof el.cloneNode !== 'function') return;
    const node = el.cloneNode(true);
    node.removeAttribute('data-testid');
    node.querySelectorAll('[id],[data-testid]').forEach((n) => { n.removeAttribute('id'); n.removeAttribute('data-testid'); });
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('inert', '');
    // Inner scroll positions, by element order (cloning keeps the order; it does not keep scrollLeft).
    const scrolled = [];
    el.querySelectorAll('*').forEach((n, i) => { if (n.scrollLeft) scrolled.push([i, n.scrollLeft]); });
    snapshot.current = { node, scrolled };
  }, [requestedWeek, shownWeek]);

  return ref;
}
